import { router, Link } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn, formatCurrency, formatDate } from '@/lib/utils'
import { CheckCircle2, RotateCcw } from 'lucide-react'
import { BackofficeOrderDetailed, BackofficeOrderLineItem, BackofficeOrderLineItemState, BackofficeOrderState } from '@/types'

const STATE_BADGES: Record<BackofficeOrderState, string> = {
  unpaid: 'bg-secondary text-secondary-foreground',
  refunded: 'bg-secondary text-secondary-foreground',
  ready: 'bg-yellow-600 text-white',
  in_progress: 'bg-blue-600 text-white',
  completed: 'bg-green-600 text-white',
}

const LINE_STATE_BADGES: Record<BackofficeOrderLineItemState, string> = {
  waiting_for_payment: 'bg-secondary text-secondary-foreground',
  refunded: 'bg-secondary text-secondary-foreground',
  ready: 'bg-yellow-600 text-white',
  in_progress: 'bg-blue-600 text-white',
  completed: 'bg-green-600 text-white',
}

const LINE_STATE_LABELS: Record<BackofficeOrderLineItemState, string> = {
  waiting_for_payment: 'Waiting for payment',
  refunded: 'Refunded',
  ready: 'Ready',
  in_progress: 'In progress',
  completed: 'Completed',
}

type Props = {
  order: BackofficeOrderDetailed
}

export default function BackofficeOrdersShow({ order }: Props) {
  return (
    <BackofficeLayout
      title={`Order ${order.id.slice(0, 8)}`}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Orders', href: '/backoffice/orders' },
        { label: order.id.slice(0, 8) },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{order.business.name}</h1>
            <Badge className={cn('font-normal text-xs', STATE_BADGES[order.state])}>{order.state.replace('_', ' ')}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {order.account.name}
            {order.origin.href ? (
              <>
                {' · '}
                <Link href={order.origin.href} className="underline underline-offset-2 hover:text-foreground">{order.origin.label}</Link>
              </>
            ) : (
              <> · {order.origin.label}</>
            )}
          </p>
        </div>

        <DetailGrid items={[
          { label: 'Submitted', value: formatDate(order.submitted_at) },
          { label: 'Paid', value: order.paid_at ? formatDate(order.paid_at) : '—' },
          { label: 'Invoice', value: order.invoice_number || '—' },
          { label: 'Receipt', value: order.receipt_number || '—' },
          { label: 'Service fee', value: formatCurrency(order.service_fee_cents) },
          { label: 'Government fee', value: formatCurrency(order.government_fee_cents) },
          { label: 'Total', value: formatCurrency(order.total_cents) },
          { label: 'Refunded', value: order.refunded_cents > 0 ? formatCurrency(order.refunded_cents) : '—' },
        ]} />

        {order.refundable_cents > 0 && order.permissions.finance_operations && (
          <div>
            <Button variant="outline" size="sm" asChild>
              <Link href={`/backoffice/orders/${order.id}/refund/new`}>
                <RotateCcw className="size-4" />
                Issue refund
              </Link>
            </Button>
          </div>
        )}
        {order.refund_blocked_by_remittance && (
          <p className="text-sm text-muted-foreground">Refunds are locked because a government transfer has already been attempted.</p>
        )}

        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Line items</p>
          <div className="border rounded-lg divide-y">
            {order.line_items.map((item) => (
              <LineItemRow key={item.id} item={item} canManageRemittance={order.permissions.finance_operations} />
            ))}
          </div>
        </div>
      </div>
    </BackofficeLayout>
  )
}

function LineItemRow({ item, canManageRemittance }: { item: BackofficeOrderLineItem; canManageRemittance: boolean }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">{item.description}</span>
            <Badge className={cn('font-normal text-xs', LINE_STATE_BADGES[item.state])}>{LINE_STATE_LABELS[item.state]}</Badge>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {formatCurrency(item.total_cents)}
            {item.term_months ? ` · ${item.term_months} months` : ''}
            {item.fulfiller ? (
              item.fulfiller.href ? (
                <>
                  {' · '}
                  <Link href={item.fulfiller.href} className="underline underline-offset-2 hover:text-foreground">{item.fulfiller.label}</Link>
                </>
              ) : (
                <> · {item.fulfiller.label}</>
              )
            ) : null}
          </div>
          {item.completion && (
            <div className="flex items-center gap-1 text-xs text-green-600 font-medium mt-1">
              <CheckCircle2 className="w-3 h-3" />
              Completed by {item.completion.completed_by} on {formatDate(item.completion.completed_at)}
            </div>
          )}
          {item.government_remittance && (
            <div className="mt-1 text-xs text-muted-foreground">
              Government remittance: {item.government_remittance.state.replace('_', ' ')}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canManageRemittance && item.government_remittance && item.government_remittance.state !== 'confirmed' && (
            <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
              <Link href={item.government_remittance.path}>Manage remittance</Link>
            </Button>
          )}
          {item.state === 'ready' && item.start_path && (
            <Button size="sm" className="h-7 text-xs" onClick={() => router.post(item.start_path!)}>
              Start work
            </Button>
          )}
          {item.state === 'in_progress' && item.complete_path && (
            <Button size="sm" className="h-7 text-xs" onClick={() => router.post(item.complete_path!)}>
              Mark complete
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
